import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AVATAR_STUDENT } from '@/utils/avatars';

interface AttendanceRecord {
    id: string;
    studentName: string;
    className: string;
    status: string;
    smsSentAt?: string | null;
}

interface AttendanceActionListProps {
    records: AttendanceRecord[];
    onSendMessage: (studentId: string) => void;
    onSendBulk: (studentIds: string[]) => void;
}

export function AttendanceActionList({ records, onSendMessage, onSendBulk }: AttendanceActionListProps) {
    const absentees = records.filter(r => r.status === 'ABSENT' || r.status === 'LATE');
    const [selected, setSelected] = useState<string[]>([]);

    const toggleSelect = (id: string) => {
        if (selected.includes(id)) {
            setSelected(selected.filter(s => s !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    const toggleAll = () => {
        if (selected.length === absentees.length) {
            setSelected([]);
        } else {
            setSelected(absentees.map(r => r.id));
        }
    };

    return (
        <Card className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h3 className="font-bold text-slate-800">Action Required</h3>
                    <p className="text-sm text-slate-500">{absentees.length} students absent or late today.</p>
                </div>
                <Button
                    onClick={() => onSendBulk(selected)}
                    disabled={selected.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                    Send Message to Selected ({selected.length})
                </Button>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead>
                        <tr>
                            <th className="px-4 py-3 text-left">
                                <input
                                    type="checkbox"
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    checked={absentees.length > 0 && selected.length === absentees.length}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Student</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Message Status</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {absentees.map((record) => (
                            <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        checked={selected.includes(record.id)}
                                        onChange={() => toggleSelect(record.id)}
                                    />
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-100 ring-2 ring-slate-200 flex-shrink-0">
                                            <img src={AVATAR_STUDENT} alt="" className="h-full w-full object-cover" />
                                        </div>
                                        <div className="font-medium text-slate-900">{record.studentName}</div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${record.status === 'ABSENT' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                                        }`}>
                                        {record.status}
                                    </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    {record.smsSentAt ? (
                                        <span className="flex items-center text-green-600 text-sm">
                                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Sent
                                        </span>
                                    ) : (
                                        <span className="text-slate-400 text-sm">Not Sent</span>
                                    )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-right">
                                    <Button
                                        variant="secondary"
                                        onClick={() => onSendMessage(record.id)}
                                        className="text-xs"
                                    >
                                        {record.smsSentAt ? 'Resend' : 'Send'}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {absentees.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                    🎉 No absences or latecomers to handle!
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}
